import React, { useState, useEffect } from "react";
import { useModalContext } from "../context/ModalContext";
import { createPortal } from "react-dom";

export interface Filters {
  categoryIds: number[];
  fontenehusIds: number[];
  allergenIds: number[];
  ingredientIds: number[];
}

interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  categories: { id: number; name: string }[];
  fontenehusList: { id: number; name: string }[];
  allergens: { id: number; name: string }[];
  ingredients: { id: number; name: string }[];
  onApplyFilters: (filters: Filters) => void;
}

const FilterModal: React.FC<FilterModalProps> = ({
  isOpen,
  onClose,
  categories,
  fontenehusList,
  allergens,
  ingredients,
  onApplyFilters,
}) => {
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
  const [selectedFontenehus, setSelectedFontenehus] = useState<number[]>([]);
  const [selectedAllergens, setSelectedAllergens] = useState<number[]>([]);
  const [selectedIngredients, setSelectedIngredients] = useState<number[]>([]);
  const [searches, setSearches] = useState({
    category: "",
    fontenehus: "",
    allergen: "",
    ingredient: "",
  });

  const { setIsAnyModalOpen } = useModalContext();

  // Update global modal state
  useEffect(() => {
    setIsAnyModalOpen(isOpen);
    return () => setIsAnyModalOpen(false);
  }, [isOpen, setIsAnyModalOpen]);

  const toggleSelection = (
    id: number,
    list: number[],
    setter: React.Dispatch<React.SetStateAction<number[]>>
  ) => {
    if (list.includes(id)) setter(list.filter((x) => x !== id));
    else setter([...list, id]);
  };

  const handleApply = () => {
    onApplyFilters({
      categoryIds: selectedCategories,
      fontenehusIds: selectedFontenehus,
      allergenIds: selectedAllergens,
      ingredientIds: selectedIngredients,
    });
    onClose();
  };

  if (!isOpen) return null;

  const sections = [
    {
      title: "Kategori",
      items: categories,
      selected: selectedCategories,
      setSelected: setSelectedCategories,
      search: searches.category,
      setSearch: (v: string) =>
        setSearches((prev) => ({ ...prev, category: v })),
    },
    {
      title: "Fontenehus",
      items: fontenehusList,
      selected: selectedFontenehus,
      setSelected: setSelectedFontenehus,
      search: searches.fontenehus,
      setSearch: (v: string) =>
        setSearches((prev) => ({ ...prev, fontenehus: v })),
    },
    {
      title: "Allergener (inneholder ikke)",
      items: allergens,
      selected: selectedAllergens,
      setSelected: setSelectedAllergens,
      search: searches.allergen,
      setSearch: (v: string) =>
        setSearches((prev) => ({ ...prev, allergen: v })),
      isAllergen: true,
    },
    {
      title: "Ingredienser",
      items: ingredients,
      selected: selectedIngredients,
      setSelected: setSelectedIngredients,
      search: searches.ingredient,
      setSearch: (v: string) =>
        setSearches((prev) => ({ ...prev, ingredient: v })),
    },
  ];

 const modalContent = (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-2 sm:p-6">
    <div className="relative w-[98vw] sm:w-[90vw] lg:w-[70vw] max-h-[95vh] bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex justify-between items-center p-4 border-b border-gray-200">
        <h2 className="text-lg sm:text-2xl font-bold text-orange-600">
          Filtrer oppskrifter
        </h2>
        <button
          onClick={onClose}
          className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200"
        >
          ×
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {sections.map((section, idx) => {
          const filtered = section.items.filter((item) =>
            item.name.toLowerCase().includes(section.search.toLowerCase())
          );
          return (
            <div key={idx}>
              <h3 className="font-semibold mb-2">{section.title}</h3>
              <input
                type="text"
                placeholder={`Søk ${section.title.toLowerCase()}...`}
                value={section.search}
                onChange={(e) => section.setSearch(e.target.value)}
                className="w-full mb-2 px-3 py-2 border rounded-md text-sm"
              />
              <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2">
                {filtered.map((item) => {
                  const selected = section.selected.includes(item.id);
                  const bgClass = section.isAllergen
                    ? selected
                      ? "bg-red-500 text-white border-transparent"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-red-100"
                    : selected
                    ? "bg-orange-500 text-white border-transparent"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-orange-100";

                  return (
                    <button
                      key={item.id}
                      onClick={() =>
                        toggleSelection(item.id, section.selected, section.setSelected)
                      }
                      className={`flex-shrink-0 px-3 py-1 rounded-full border text-sm ${bgClass}`}
                    >
                      {item.name}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="flex gap-3 p-4 border-t border-gray-200">
        <button
          onClick={onClose}
          className="flex-1 py-3 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100"
        >
          Avbryt
        </button>
        <button
          onClick={handleApply}
          className="flex-1 py-3 rounded-lg bg-orange-500 text-white hover:bg-orange-600"
        >
          Bruk filtre
        </button>
      </div>
    </div>
  </div>
);

// use createPortal to render at body level
return createPortal(modalContent, document.body);
};

export default FilterModal;
